import { useMemo, useState, useRef, memo } from 'react';
import { ChevronDown, ChevronUp, LogIn } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  type BranchReportEntry,
  getTotalEmrFilled,
  getTotalExternalCompletion,
  getPendingVisits,
  getRogerVsExternalPercentage,
} from '../../types/adoptionReport.types';
import {
  getPendingVisits as getVisitPendingVisits,
  getRogerVsExternalPercentage as getVisitRogerVsExternalPercentage,
} from '../../types/visitCompletion.types';
import { getClinicianFullName } from '../../types/clinician.types';
import PageLoader from '../PageLoader';

// Keyed rather than positional: the Phone column is conditional, so index-based
// widths would silently shift every column after it when phone is hidden.
const COLUMN_WIDTHS = {
  name: '250px',
  title: '150px',
  phone: '150px',
  branch: '250px',
  pending: '220px',
  roger: '220px',
  external: '250px',
  rogerPercentage: '220px',
} as const;

const PHONE_COLUMN_COUNT = 1;
const VISIBLE_COLUMN_COUNT = Object.keys(COLUMN_WIDTHS).length;

const totalWidthFor = (showPhone: boolean): number =>
  Object.entries(COLUMN_WIDTHS).reduce(
    (sum, [key, width]) =>
      key === 'phone' && !showPhone ? sum : sum + parseInt(width, 10),
    0
  );

const SortableHeader = ({
  column,
  children,
  width,
  sortColumn,
  sortDirection,
  onSort,
}: {
  column: string;
  children: React.ReactNode;
  width: string;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
}) => (
  <th
    className='cursor-pointer select-none bg-muted border-b-2 border-border p-3 text-left font-semibold text-sm sticky top-0 z-10 hover:bg-muted/80 transition-colors'
    style={{ width, minWidth: width, maxWidth: width }}
    onClick={() => onSort(column)}
  >
    <div className='flex items-center justify-between pointer-events-none'>
      <span className='font-semibold'>{children}</span>
      {sortColumn === column && sortDirection === 'asc' ? (
        <ChevronUp className='h-4 w-4' />
      ) : sortColumn === column && sortDirection === 'desc' ? (
        <ChevronDown className='h-4 w-4' />
      ) : (
        <ChevronDown className='h-4 w-4 opacity-30' />
      )}
    </div>
  </th>
);

const VirtualRow = memo(
  ({
    entry,
    showBreakdown,
    phone,
  }: {
    entry: BranchReportEntry;
    showBreakdown: boolean;
    /** null when the caller may not read phone numbers, which hides the cell. */
    phone: string | null;
  }) => {
    const totalPercentage = getRogerVsExternalPercentage(entry) + '%';

    return (
      <>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS.name,
            minWidth: COLUMN_WIDTHS.name,
            maxWidth: COLUMN_WIDTHS.name,
          }}
        >
          <div className='flex items-center justify-between gap-2 overflow-hidden'>
            <span
              className='font-medium truncate'
              title={getClinicianFullName(entry.clinician)}
            >
              {getClinicianFullName(entry.clinician)}
            </span>
            {!entry.clinician.hasLoggedIn && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className='shrink-0'>
                      <LogIn className='h-4 w-4 text-destructive cursor-help' />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This user has never logged into the app</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS.title,
            minWidth: COLUMN_WIDTHS.title,
            maxWidth: COLUMN_WIDTHS.title,
          }}
        >
          <span className='text-sm truncate block'>
            {entry.clinician.title || '-'}
          </span>
        </td>
        {phone !== null && (
          <td
            className='p-3 border-b border-border'
            style={{
              width: COLUMN_WIDTHS.phone,
              minWidth: COLUMN_WIDTHS.phone,
              maxWidth: COLUMN_WIDTHS.phone,
            }}
          >
            <span className='text-sm truncate block'>{phone || '-'}</span>
          </td>
        )}
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS.branch,
            minWidth: COLUMN_WIDTHS.branch,
            maxWidth: COLUMN_WIDTHS.branch,
          }}
        >
          <span
            className='text-sm font-medium truncate block'
            title={entry.branchName}
          >
            {entry.branchName}
          </span>
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS.pending,
            minWidth: COLUMN_WIDTHS.pending,
            maxWidth: COLUMN_WIDTHS.pending,
          }}
        >
          {showBreakdown ? (
            <div className='flex items-center gap-2'>
              <span className='font-medium'>{getPendingVisits(entry)}</span>
              <div className='text-xs text-muted-foreground leading-tight'>
                <div>{getVisitPendingVisits(entry.oasis)} OASIS</div>
                <div>{getVisitPendingVisits(entry.regular)} Regular</div>
              </div>
            </div>
          ) : (
            <span className='font-medium'>{getPendingVisits(entry)}</span>
          )}
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS.roger,
            minWidth: COLUMN_WIDTHS.roger,
            maxWidth: COLUMN_WIDTHS.roger,
          }}
        >
          {showBreakdown ? (
            <div className='flex items-center gap-2'>
              <span className='font-medium'>{getTotalEmrFilled(entry)}</span>
              <div className='text-xs text-muted-foreground leading-tight'>
                <div>{entry.oasis.emrFilled} OASIS</div>
                <div>{entry.regular.emrFilled} Regular</div>
              </div>
            </div>
          ) : (
            <span className='font-medium'>{getTotalEmrFilled(entry)}</span>
          )}
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS.external,
            minWidth: COLUMN_WIDTHS.external,
            maxWidth: COLUMN_WIDTHS.external,
          }}
        >
          {showBreakdown ? (
            <div className='flex items-center gap-2'>
              <span className='font-medium'>
                {getTotalExternalCompletion(entry)}
              </span>
              <div className='text-xs text-muted-foreground leading-tight'>
                <div>{entry.oasis.externalCompletion} OASIS</div>
                <div>{entry.regular.externalCompletion} Regular</div>
              </div>
            </div>
          ) : (
            <span className='font-medium'>
              {getTotalExternalCompletion(entry)}
            </span>
          )}
        </td>
        <td
          className='p-3 border-b border-border'
          style={{
            width: COLUMN_WIDTHS.rogerPercentage,
            minWidth: COLUMN_WIDTHS.rogerPercentage,
            maxWidth: COLUMN_WIDTHS.rogerPercentage,
          }}
        >
          {showBreakdown ? (
            <div className='flex items-center gap-2'>
              <span className='font-medium'>{totalPercentage}</span>
              <div className='text-xs text-muted-foreground leading-tight'>
                <div>
                  {getVisitRogerVsExternalPercentage(entry.oasis)}% OASIS
                </div>
                <div>
                  {getVisitRogerVsExternalPercentage(entry.regular)}% Regular
                </div>
              </div>
            </div>
          ) : (
            <span className='font-medium'>{totalPercentage}</span>
          )}
        </td>
      </>
    );
  }
);
VirtualRow.displayName = 'VirtualRow';

export interface VisitCompletionByClinicianAndBranchReportTableProps {
  data: BranchReportEntry[];
  loading: boolean;
  error: string | null;
  showBreakdown: boolean;
  hideNeverLoggedIn: boolean;
  searchTerm: string;
  branchesLoading: boolean;
  hasBranchSelection: boolean;
  /** Omitted when the caller may not read phone numbers, which drops the column. */
  phonesByUsername?: Map<string, string>;
}

const VisitCompletionByClinicianAndBranchReportTable = ({
  data,
  loading,
  error,
  showBreakdown,
  hideNeverLoggedIn,
  searchTerm,
  branchesLoading,
  hasBranchSelection,
  phonesByUsername,
}: VisitCompletionByClinicianAndBranchReportTableProps) => {
  const [sortColumn, setSortColumn] = useState<string>('rogerPercentage');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const parentRef = useRef<HTMLDivElement>(null);

  const showPhone = phonesByUsername !== undefined;
  const phoneFor = (entry: BranchReportEntry): string =>
    phonesByUsername?.get(entry.clinician.username.toLowerCase()) ?? '';

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      if (
        ['pending', 'roger', 'external', 'rogerPercentage'].includes(column)
      ) {
        setSortDirection('desc');
      } else {
        setSortDirection('asc');
      }
    }
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = [...data];

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => {
        const fullName = getClinicianFullName(entry.clinician).toLowerCase();
        const firstName = entry.clinician.firstName.toLowerCase();
        const lastName = entry.clinician.lastName.toLowerCase();
        const title = (entry.clinician.title || '').toLowerCase();
        const branch = entry.branchName.toLowerCase();
        const phone = phoneFor(entry).toLowerCase();

        return (
          fullName.includes(searchLower) ||
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          title.includes(searchLower) ||
          branch.includes(searchLower) ||
          (phone !== '' && phone.includes(searchLower))
        );
      });
    }

    if (hideNeverLoggedIn) {
      filtered = filtered.filter(entry => entry.clinician.hasLoggedIn);
    }

    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortColumn) {
          case 'name':
            aValue =
              `${a.clinician.lastName}, ${a.clinician.firstName}`.toLowerCase();
            bValue =
              `${b.clinician.lastName}, ${b.clinician.firstName}`.toLowerCase();
            break;
          case 'title':
            aValue = (a.clinician.title || '').toLowerCase();
            bValue = (b.clinician.title || '').toLowerCase();
            break;
          case 'phone':
            aValue = phoneFor(a);
            bValue = phoneFor(b);
            break;
          case 'branch':
            aValue = a.branchName.toLowerCase();
            bValue = b.branchName.toLowerCase();
            break;
          case 'pending':
            aValue = getPendingVisits(a);
            bValue = getPendingVisits(b);
            break;
          case 'roger':
            aValue = getTotalEmrFilled(a);
            bValue = getTotalEmrFilled(b);
            break;
          case 'external':
            aValue = getTotalExternalCompletion(a);
            bValue = getTotalExternalCompletion(b);
            break;
          case 'rogerPercentage': {
            const aTotal = getTotalEmrFilled(a) + getTotalExternalCompletion(a);
            aValue = aTotal === 0 ? 0 : getTotalEmrFilled(a) / aTotal;

            const bTotal = getTotalEmrFilled(b) + getTotalExternalCompletion(b);
            bValue = bTotal === 0 ? 0 : getTotalEmrFilled(b) / bTotal;
            break;
          }
          default:
            return 0;
        }

        if (typeof aValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue as string)
            : (bValue as string).localeCompare(aValue);
        } else {
          return sortDirection === 'asc'
            ? aValue - (bValue as number)
            : (bValue as number) - aValue;
        }
      });
    } else {
      filtered.sort((a, b) => {
        const nameCompare = a.clinician.username.localeCompare(
          b.clinician.username
        );
        if (nameCompare !== 0) return nameCompare;
        return a.branchName.localeCompare(b.branchName);
      });
    }

    return filtered;
  }, [
    data,
    searchTerm,
    sortColumn,
    sortDirection,
    hideNeverLoggedIn,
    phonesByUsername,
  ]);

  const virtualizer = useVirtualizer({
    count: filteredAndSortedData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60,
    overscan: 5,
  });

  if (branchesLoading) {
    return (
      <div className='flex-1 overflow-hidden min-h-[200px] flex items-center justify-center'>
        <PageLoader message='Loading branches...' />
      </div>
    );
  }

  if (!hasBranchSelection) {
    return (
      <div className='flex-1 overflow-hidden min-h-0 flex items-center justify-center'>
        <div className='text-lg font-medium text-muted-foreground'>
          Please select one or more branches to view the report
        </div>
      </div>
    );
  }

  return (
    <div className='flex-1 overflow-hidden min-h-0'>
      {error && (
        <Alert variant='destructive' className='mb-4'>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div ref={parentRef} className='h-full overflow-auto pb-[50px] relative'>
        {loading ? (
          <div className='absolute inset-0 flex items-center justify-center bg-background/80 z-20'>
            <PageLoader message='Loading adoption report data...' />
          </div>
        ) : null}

        <div
          style={{ width: '100%', minWidth: `${totalWidthFor(showPhone)}px` }}
        >
          <table className='w-full border-collapse bg-background table-fixed'>
            <thead className='sticky top-0 z-10 bg-muted/50 shadow-sm'>
              <tr>
                <SortableHeader
                  column='name'
                  width={COLUMN_WIDTHS.name}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Clinician Name
                </SortableHeader>
                <SortableHeader
                  column='title'
                  width={COLUMN_WIDTHS.title}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Title
                </SortableHeader>
                {showPhone && (
                  <SortableHeader
                    column='phone'
                    width={COLUMN_WIDTHS.phone}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Phone
                  </SortableHeader>
                )}
                <SortableHeader
                  column='branch'
                  width={COLUMN_WIDTHS.branch}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Branch
                </SortableHeader>
                <SortableHeader
                  column='pending'
                  width={COLUMN_WIDTHS.pending}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Pending Visits
                </SortableHeader>
                <SortableHeader
                  column='roger'
                  width={COLUMN_WIDTHS.roger}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Completed in Roger
                </SortableHeader>
                <SortableHeader
                  column='external'
                  width={COLUMN_WIDTHS.external}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Completed Outside of Roger
                </SortableHeader>
                <SortableHeader
                  column='rogerPercentage'
                  width={COLUMN_WIDTHS.rogerPercentage}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  % Completed in Roger
                </SortableHeader>
              </tr>
            </thead>

            <tbody>
              <tr style={{ height: `${virtualizer.getTotalSize()}px` }}>
                <td
                  colSpan={
                    showPhone
                      ? VISIBLE_COLUMN_COUNT
                      : VISIBLE_COLUMN_COUNT - PHONE_COLUMN_COUNT
                  }
                  style={{ padding: 0 }}
                >
                  <div
                    style={{
                      height: `${virtualizer.getTotalSize()}px`,
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    {virtualizer.getVirtualItems().map(virtualItem => {
                      const entry = filteredAndSortedData[virtualItem.index];
                      return (
                        <div
                          key={virtualItem.key}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${virtualItem.size}px`,
                            transform: `translateY(${virtualItem.start}px)`,
                          }}
                        >
                          <table className='w-full border-collapse table-fixed'>
                            <tbody>
                              <tr
                                className={
                                  virtualItem.index % 2 === 0
                                    ? 'bg-muted/30'
                                    : 'bg-background'
                                }
                              >
                                <VirtualRow
                                  entry={entry}
                                  showBreakdown={showBreakdown}
                                  phone={showPhone ? phoneFor(entry) : null}
                                />
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VisitCompletionByClinicianAndBranchReportTable;
